/**
 * Live collector fleet health and rollout-progress pushes, delivered through
 * the shared `collectors` room.
 */

import type { CollectorRolloutEvent, CollectorStatusEvent } from '@/types/collectors'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

export class CollectorsLiveApi {
  private readonly status: SubscriptionRegistry<null, CollectorStatusEvent>
  private readonly rollout: SubscriptionRegistry<null, CollectorRolloutEvent>

  constructor(private readonly client: WebSocketClient) {
    // collector_status_updated and collector_rollout_updated both ride the
    // shared `collectors` room. Each registry joins on its own first subscriber
    // (the server-side join is idempotent), but the room must be LEFT only once
    // NEITHER event has a subscriber left — otherwise unsubscribing one (e.g.
    // unmounting the rollout panel while the fleet table stays mounted) would
    // leave the room and silently drop the other event's pushes too.
    this.status = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('join_collectors', {}),
      leave: () => this.leaveRoomIfIdle(),
    })
    this.rollout = new SubscriptionRegistry({
      join: () => this.client.emitWhenConnected('join_collectors', {}),
      leave: () => this.leaveRoomIfIdle(),
    })
  }

  /**
   * Leave the shared collectors room only when neither the status nor the
   * rollout registry has a remaining subscriber. A registry calls this from its
   * unsubscribe after it has already deleted its own now-empty key, so this
   * observes the post-removal state of both registries.
   */
  private leaveRoomIfIdle(): void {
    if (!this.status.hasSubscribers(null) && !this.rollout.hasSubscribers(null)) {
      this.client.emitIfSocket('leave_collectors', {})
    }
  }

  /**
   * Subscribe to collector health pushes. Joins the `collectors` room when
   * the subscriber set goes empty → non-empty and leaves it when the last
   * subscriber unsubscribes.
   */
  subscribeToCollectorStatus(callback: (event: CollectorStatusEvent) => void): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    this.client.attachOnce('collector_status_updated', (event) => {
      this.status.dispatch(null, event as CollectorStatusEvent)
    })

    return this.status.subscribe(null, callback)
  }

  /**
   * Subscribe to rollout-progress pushes (operator PUT broadcasts
   * collector_rollout_updated; Phase-4 auto-advances are surfaced via poll).
   */
  subscribeToRolloutProgress(callback: (event: CollectorRolloutEvent) => void): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    this.client.attachOnce('collector_rollout_updated', (event) => {
      this.rollout.dispatch(null, event as CollectorRolloutEvent)
    })

    return this.rollout.subscribe(null, callback)
  }

  /** Rejoin the collectors room after a reconnect. */
  replay(): void {
    this.status.replay()
    this.rollout.replay()
  }

  clear(): void {
    this.status.clear()
    this.rollout.clear()
  }
}
